/**
 * 实时通信中枢常量定义。
 *
 * 集中维护对外可见的状态枚举、内置事件名与默认配置，方便业务侧引用与替换。
 * @module utils/realtime/constants
 */

/**
 * 连接状态枚举。
 *
 * - `idle`：未连接（尚未触发 connect / 已主动 disconnect）。
 * - `connecting`：WebSocket 正在建立。
 * - `open`：连接已就绪，可正常收发。
 * - `closed`：被远端或网络关闭，可能正在尝试重连。
 * - `reconnecting`：进入重连尝试阶段。
 * - `failed`：达到最大重连次数后放弃。
 */
export const RealtimeStatus = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  OPEN: 'open',
  CLOSED: 'closed',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed'
});

/**
 * Hub 对外暴露的内置事件名。
 *
 * 业务侧使用 `realtimeHub.on(event, handler)` 监听这些事件，与「按 topic 订阅业务消息」是两套通道。
 */
export const RealtimeEvent = Object.freeze({
  /** 连接状态变更，参数 (status: RealtimeStatus 值) */
  STATUS: 'status',
  /** 重连成功（首次连接成功也会触发 `open`，但不会触发 `reconnect`） */
  RECONNECT: 'reconnect',
  /** 任意未被请求/响应消费的原始消息，参数 (payload: object) */
  MESSAGE: 'message',
  /** 通用错误，参数 (error: Error | string) */
  ERROR: 'error'
});

/**
 * 内置协议约定。
 *
 * - 后端推送的消息默认为 `{ type | businessType, data, requestId? }` 结构。
 * - 若消息携带 `requestId` 且命中前端 pendingRequests，则走请求/响应分支；
 *   否则按 `type`（缺省时 `businessType`）派发给 topic 订阅者。
 */
export const ProtocolField = Object.freeze({
  TYPE: 'type',
  BUSINESS_TYPE: 'businessType',
  DATA: 'data',
  REQUEST_ID: 'requestId'
});

/** 默认请求超时时间（毫秒） */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/** 浏览器 `online` 事件后延迟重连的默认时间（毫秒），与下载中心等模块一致，避免误触 */
export const DEFAULT_NETWORK_ONLINE_DEBOUNCE_MS = 1500;

/**
 * Hub 默认配置。
 *
 * - `connect`: `'lazy'` 表示在首次 subscribe/send/request 时才发起连接，`'eager'` 表示构造完即连。
 */
export const DEFAULT_HUB_OPTIONS = Object.freeze({
  /** @type {'lazy' | 'eager'} */
  connect: 'lazy',
  /** 默认 request 超时时间（毫秒） */
  requestTimeoutMs: DEFAULT_REQUEST_TIMEOUT_MS
});
